/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.katalyst

import io.peekandpoke.klang.audio_be.Oversampler
import io.peekandpoke.klang.audio_bridge.KatalystDsl
import io.peekandpoke.klang.common.infra.KlangMap
import kotlin.math.roundToInt

/**
 * Writes a chain's own slots into ONE built stage, with no voice in sight: the chain is the
 * instrument (the signal-flow plan's D4).
 *
 * **The ONLY kind of writer there is** since Katalyst step 5b-1 (2026-09-19): the chain a cylinder
 * is born with reads the orbit's param state exactly as a declared one does, and the owner-voice
 * writers that read the bus FIELDS are gone. A chain can therefore be configured with NO OWNER
 * ALIVE (`KatalystChain.applyParams(null)`), which is what a chain faded in from `Cylinders`'
 * pending poll needs: the block's voices have already offered themselves by then, and without this
 * the chain would run its first blocks at the settings [KatalystChain.reset] left.
 *
 * **Two methods, because the orbit's param state changes far more rarely than a block goes by**
 * (Katalyst step 5a). [resolve] re-reads this stage's [KatalystKnob]s from the state and refills
 * whatever composite the stage wants (a `ResonatorConfig`, a [CompressorSettings]); [apply] writes what is
 * already resolved into the stage and does no lookup and no allocation, so it can run on every
 * block. [KatalystChain.applyParams] is the one place that decides
 * which of the two a block needs.
 *
 * Stateful, and that IS the design: the resolved numbers have to live somewhere between the map
 * that produced them and the block that writes them, and the stage they belong to takes several of
 * them at once.
 */
internal interface KatalystSlotWriter {
    /** Re-read this stage's slots from the orbit's param state. Null = the authored defaults. */
    fun resolve(params: KlangMap<Double>?)

    /** Write the resolved values into the stage. No map, no allocation, idempotent. */
    fun apply()

    /**
     * True when a knob this writer ticks holds a modulator ([KatalystKnob.isModulated]), so the chain must call [tick]
     * and [apply] on every block it processes ([KatalystChain.process]). Fixed at build.
     */
    val isModulated: Boolean

    /**
     * One block of this writer's modulated knobs (knob expressions step 5b): each ticks ([KatalystKnob.tick]: a run,
     * then its clocks one block on), then the writer refills what [apply] hands over. Called only when [isModulated],
     * once per processed block, by [KatalystChain.process]; no map, no allocation.
     */
    fun tick()
}

/**
 * A built orbit chain: the stage instances for one [KatalystDsl], in the DSL's order, plus the
 * lifecycle the hosting cylinder needs.
 *
 * Instances are **stateful** (delay ring, reverb network, compressor envelope, phaser sweep
 * clock), so a chain belongs to exactly one cylinder and is never shared. Building can rent from
 * the warehouse, so a chain is built ONCE per cylinder, never per block and never per voice (see
 * `Cylinder`), and one per output chain in `MasterBus`, which runs the same class at the output.
 *
 * **The duck is not in [pipeline].** It needs every orbit processed first, so `Cylinders` runs it
 * after the chains, which is what it has always done; the duck stage's position in the DSL list is
 * therefore ignored, and a chain that declares two keeps the last (decided 2026-09-17).
 *
 * Every knob comes from the orbit's param state through [applyParams], the chain a cylinder is
 * born with included (step 5b-1). What the DSL moved is WHERE the stage list lives: it is data
 * now, not a hardcoded `listOf(...)`.
 */
class KatalystChain internal constructor(
    /**
     * The stages that run on the orbit mix per block, in DSL order, as the array [process] walks.
     * The duck is deliberately absent (see the class KDoc); [stages] is the whole set the chain
     * owns.
     *
     * Array, not List: iterated once per block per orbit, so no iterator
     * allocation. Private, because an array hands every reader a write: what leaves the chain is
     * the read-only [pipeline] view.
     */
    private val serial: Array<KatalystEffect>,
    /** One writer per declared stage, in DSL order, the duck's last. See [KatalystSlotWriter]. */
    private val writers: Array<KatalystSlotWriter>,
    /** The duck stage, or null when the chain declares none. The LAST declared duck wins. */
    val duck: KatalystDuckEffect?,
    /**
     * The writer of this chain's [duck] stage, or null when it declares none (see [ducksWith], the
     * one reader). Its `declared` flag is whether the stage currently resolves to settings, which
     * `.katp` can change while the chain runs.
     */
    private val duckWriter: KatalystDuckWriter?,
    /**
     * The 2x steps above the base rate this chain runs at: 0 for an orbit's or the master's chain, the region's for the
     * body of an oversampling region (KLANG-00214 (oversampling regions)). Every oversampler in [oversamplers] starts
     * here, so [pureDelayFrames] counts them from it.
     */
    val firstStage: Int = 0,
) {
    /**
     * The processing order as a read-only view, for the hosts and the specs: `List`, not the
     * array, so no caller can swap a stage out from under the render path. Created once here.
     */
    val pipeline: List<KatalystEffect> = serial.asList()

    /**
     * Every stage this chain owns, the duck included: the set the lifecycle covers. Built once
     * here, never on a render path, and always a COPY, so the lifecycle array and the processing
     * array can never alias (a chain without a duck would otherwise share one array with two
     * meanings, and the next reader to append to "the lifecycle set" would change the pipeline).
     */
    private val stages: Array<KatalystEffect> = if (duck == null) serial.copyOf() else serial + duck

    /**
     * The writers with a modulated knob ([KatalystSlotWriter.isModulated]), in [writers]' order: the ones [process]
     * ticks and applies at the top of every block. Empty for a chain without a modulator, which therefore processes
     * exactly as before step 5b. A `parallel` stage's writer is never here: each branch is a chain and ticks its own
     * in its own [process], which the stage calls once per block.
     */
    private val modulated: Array<KatalystSlotWriter> = writers.filter { it.isModulated }.toTypedArray()

    /**
     * Test seam: how many writers the build installed. The one way a spec can tell "the dropped
     * duplicate has no writer" from "it has one that nothing runs", which is what the last-duck
     * rule turns on.
     */
    internal val writerCount: Int get() = writers.size

    /**
     * The param state [writers] last resolved from, by REFERENCE: the gate of [applyParams]. Null
     * both before anything resolved (see [everResolved]) and after a resolve from no owner.
     */
    private var resolvedFrom: KlangMap<Double>? = null

    /** False until the first [applyParams], so that resolving from a null state still happens. */
    private var everResolved: Boolean = false

    /** Test seam: how many times [applyParams] actually re-read the state. */
    internal var resolveCount: Int = 0
        private set

    // ════════════════════════════════════════════════════════════════════════════
    // Chain properties, computed once at build
    // ════════════════════════════════════════════════════════════════════════════

    // No typed accessor for a serial stage is left on the chain (the duck, which runs outside [pipeline], keeps its
    // own): production walks [pipeline] and asks the questions below, which test stages by kind once at build. The
    // specs that ask about ONE stage (the warehouse specs about the rented ring and network, the resonator specs about
    // a body and a vowel) find it through their own helpers (`_katalyst_test_helpers.kt`, engine tidy-up step 13).

    /**
     * True when this chain declares a stage that can ring on after its input stops: the reverb, the delay, and the
     * distort stage (its DC blocker decays from the offset an asymmetric shape made, `KatalystDistortEffect.hasTail`;
     * review round 2 of KLANG-00292 (katalyst distort stage): without it a stopped playback's master cut that decay).
     * A `parallel` stage declares one when a branch does; a region always does: whatever its body holds, its down
     * conversion rings out what the body last produced (`KatalystRegionEffect.hasTail`'s hold).
     * The master's "is a tail possible at all" test (`MasterBus.isRinging`), cheap, no buffer scan; [hasTail] then
     * answers whether one is there. Body, vowel and phaser may ring too; whether that is audible is
     * KLANG-00293 (chain swap cuts ringing inserts).
     */
    val declaresTail: Boolean = serial.any {
        it is KatalystReverbEffect || it is KatalystDelayEffect || it is KatalystDistortEffect ||
                (it is KatalystParallelEffect && it.declaresTail) || it is KatalystRegionEffect
    }

    /**
     * Frames this chain delays the orbit by: the sum of its latent stages' delays ([KatalystLatentEffect]: a
     * compressor's lookahead, phase 3 step 12 C2, an oversampling region, and a `parallel` holding either), since every stage runs in
     * series on the one mix. 0 for a chain without one. A chain swap delays its ramps by the later of the two
     * chains' latencies (`ChainSwap.begin`).
     */
    val latencyFrames: Int = serial.sumOf { (it as? KatalystLatentEffect)?.latencyFrames ?: 0 }

    /** [latencyFrames] before each stage rounded it ([KatalystLatentEffect.exactLatency]), for an oversampling region. */
    val exactLatency: Double = serial.sumOf { (it as? KatalystLatentEffect)?.exactLatency ?: 0.0 }

    /**
     * The oversamplers inside [latencyFrames] ([KatalystLatentEffect.oversamplers]), ascending, for a `parallel` stage;
     * each starts at [firstStage].
     */
    val oversamplers: List<Int> = serial.flatMap { (it as? KatalystLatentEffect)?.oversamplers.orEmpty() }.sorted()

    /**
     * The part of [latencyFrames] that is a pure delay (a compressor's lookahead, a region's body): what the oversamplers
     * leave of [exactLatency], rounded once, so a `parallel` pads it by the nearest whole frame. Without a region it is
     * the whole-frame lookaheads, as before.
     */
    val pureDelayFrames: Int =
        (exactLatency - oversamplers.sumOf { Oversampler.groupDelaySamples(stages = it, firstStage = firstStage) }).roundToInt()

    /**
     * The region that begins an orbit's chain, which takes the orbit's rate buses in at its rate (KLANG-00214
     * (oversampling regions) step 3b), or null: the chain does not begin with a region, or it is a region's body.
     */
    private val busHead: KatalystRegionEffect? = (serial.firstOrNull() as? KatalystRegionEffect)?.takeIf { firstStage == 0 }

    /**
     * The rate this chain runs an orbit at, in 2x steps above the base rate: its head region's (section 10, "Orbits":
     * what the first stage asks for on a base-rate input), 0 for a chain that begins with anything else.
     */
    private val rateSteps: Int = busHead?.steps ?: 0

    /**
     * Steps the orbit's rate buses above [rateSteps] down to it. Built for every chain at the base rate, a master's and a
     * `parallel` branch's too, whose contexts never hold a bus (the ladder then returns at once); none in a region's body.
     */
    private val busLadder: KatalystBusLadder? = if (firstStage == 0) KatalystBusLadder(level = rateSteps) else null

    /** Rents the warehouse refused any stage of this chain, for the diagnostics feedback. */
    val deniedRents: Int
        get() {
            var total = 0

            for (i in stages.indices) {
                total += stages[i].deniedRents
            }

            return total
        }

    // ════════════════════════════════════════════════════════════════════════════
    // Per block
    // ════════════════════════════════════════════════════════════════════════════

    /**
     * Apply EVERY stage of this chain from the orbit's param state ([params], the owner voice's
     * `katalystParams`). **Null is the no-owner door**: no state, so every slot resolves to what
     * the chain itself authored, which is what a chain entering service needs before any voice has
     * owned the orbit, and what a chain arriving on an orbit without an owner resolves from.
     *
     * **This is the ONE way a bus knob reaches a stage** since step 5b-1, on a declared chain and
     * on the chain a cylinder is born with alike. The rule and its one home are the `katalystParam` door's
     * KDoc in `sprudel/lang/lang_katalyst.kt`.
     *
     * Absent stages are turned OFF, so the state fully determines the orbit and nothing leaks from
     * a previous owner. The values are re-written every block and that is idempotent (body and
     * vowel short-circuit an unchanged config). The compressor and ducking INSTANCES are reused,
     * so their envelope followers survive across notes as long as consecutive owners keep the
     * effect: a takeover by a voice that asks for neither clears it (the compressor by gliding its
     * gain reduction to 0 dB first, see [KatalystCompressorEffect]), and the next owner that
     * re-adds it after that starts a fresh envelope.
     *
     * Stage by stage, in DSL order. The order is unobservable (each stage writes only its own
     * instance, and no stage reads another's params), which is why the historical
     * `applyBusEffects` could configure ducking before the compressor while the classic chain
     * declares them the other way round.
     *
     * **The re-resolve is gated on the map's IDENTITY, the apply is not** (Katalyst step 5a). A
     * live owner hands over the same map instance every block, so the lookups run once per owner
     * and the blocks in between cost exactly what step 3a's fixed writers cost: one virtual call
     * per stage, writing numbers that are already in hand. The values are re-WRITTEN every block
     * regardless, because that is what makes a writer idempotent after a [reset] the orbit reached
     * while the same voice still owned the orbit.
     *
     * A map is immutable by contract (`VoiceData.katalystParams`), so identity is a sound test for
     * "these are the same values"; a producer that mutated one in place would be breaking that
     * contract, not this gate. [everResolved] is what makes the FIRST call resolve even when there
     * is nothing to compare against.
     *
     * The reference is dropped by [reset] and [retire], so an idle chain pins no voice's map (the
     * allocation-cleanup rule).
     */
    fun applyParams(params: KlangMap<Double>?) {
        resolveParams(params)

        for (i in writers.indices) {
            writers[i].apply()
        }
    }

    /**
     * The RESOLVE half of [applyParams], without the write: what a host needs when it has to ASK
     * this chain something that depends on its slots before its writers may run.
     *
     * The callers are the two swap paths (`Cylinder.beginFade` and the late-duck correction in
     * `Cylinder.commitOwner`), which ask [ducksWith] before a live envelope is handed over and
     * must NOT write the stages first: the carried envelope is updated in place by the arriving
     * chain's own writer AFTER the handover (see [KatalystDuckEffect.configure] and
     * [KatalystDuckEffect.takeOver]).
     * Without this, a chain whose duck is named by `.katp("duck.orbit", n)` still reads as "no
     * duck" at the moment the question is asked, and the swap ramps the reduction out and then
     * drops a fresh one on the orbit a block later.
     *
     * Idempotent and gated exactly like [applyParams], so the [applyParams] that follows on the
     * same map instance costs nothing extra.
     */
    fun resolveParams(params: KlangMap<Double>?) {
        if (everResolved && params === resolvedFrom) {
            return
        }

        everResolved = true
        resolvedFrom = params
        resolveCount++

        for (i in writers.indices) {
            writers[i].resolve(params)
        }
    }

    /**
     * Runs the serial chain on the orbit's buffers, in DSL order.
     *
     * **First, the modulated knobs move** (knob expressions step 5b, "the hook"): each writer in [modulated] ticks its
     * knobs one block and applies, before any stage runs. HERE and not in [applyParams], because a chain processes
     * whenever it is active, and only an owned orbit is configured: an orbit with no owner, the master (configured
     * with no state), the leaving chain of a swap through its fade AND its drain (Q4: its modulators keep running, its
     * slots stay as its last resolve left them), and every `parallel` branch (the stage calls this once per branch per
     * block) all come through here, once per block. The clocks therefore run over the blocks this chain processed
     * (Q3): they pause while it does not, and [reset] does not rewind them.
     *
     * The order with a changed map: the host's [applyParams] resolves first (a run at the clocks as they stand), then
     * this tick runs and advances once, so a block never advances a clock twice. The [apply] after the tick writes this
     * block's values; on an owned orbit the host's own [applyParams] earlier in the block wrote the previous block's,
     * which every stage takes as an unchanged config (a retarget to the target that stands, a compare that matches).
     */
    fun process(ctx: KatalystContext) {
        tickModulated()

        // The orbit's rate buses meet at the chain's rate (step 3b): in the head region, or stepped down into the mix.
        val head = busHead
        var first = 0

        if (head != null) {
            head.process(ctx = ctx, ladder = busLadder)
            first = 1
        } else {
            busLadder?.addAbove(ctx = ctx, target = ctx.mixBuffer)
        }

        for (i in first until serial.size) {
            serial[i].process(ctx)
        }

        // The orbit has rendered a block with this chain in it. The duck is not in [serial] and
        // does not run at all while it is off, so this is the one per-block event it can see, and
        // it is what closes its "the first initialisation is instant" window: a duck that engages
        // at bar five must fade in rather than snap (see [KatalystDuckEffect.orbitBlockRan]).
        duck?.orbitBlockRan()
    }

    /** The hook of [process]: every modulated writer ticks one block, then applies. Nothing for a chain without one. */
    private fun tickModulated() {
        for (i in modulated.indices) {
            val writer = modulated[i]

            writer.tick()
            writer.apply()
        }
    }

    /**
     * Runs the duck, which the host calls AFTER every orbit has been processed (the sidechain
     * source is another cylinder's mix). A chain without a duck stage does nothing here.
     */
    fun processDuck(ctx: KatalystContext) {
        duck?.process(ctx)
    }

    // ════════════════════════════════════════════════════════════════════════════
    // Lifecycle
    // ════════════════════════════════════════════════════════════════════════════

    /**
     * Turn every stage off AND clear its internal state: the clean slate a cylinder reaches when
     * its orbit deactivates (the owner is forgotten), so a reused orbit never replays a previous
     * owner's tail.
     */
    fun reset() {
        forgetParams()
        busLadder?.reset()

        for (i in stages.indices) {
            stages[i].reset()
        }
    }

    /**
     * Hand this chain to the shelf: the rented units (delay ring, reverb network) go back to THEIR
     * shelves and the small stages clear.
     *
     * NOT [reset]: that would zero the ring and the network here, on the audio thread, and the
     * shelves zero them again on return (`Cylinder.retire`'s review round 3: sixteen warmup
     * cylinders retired in one block were ~19 MB of stores, twice). The units go back DIRTY and
     * the warehouse's housekeeping zeroes them a block at a time.
     */
    fun retire() {
        forgetParams()
        busLadder?.reset()

        for (i in stages.indices) {
            stages[i].retire()
        }
    }

    /**
     * True when this chain's [duck] will be CONFIGURED the next time its writers run, rather than
     * cleared or left alone.
     *
     * A chain that declares a Duck stage does not necessarily duck: one whose `orbit` slot is unset
     * resolves to no settings at all. The host asks this before it hands a live envelope to an
     * ARRIVING chain (`Cylinder.handOverDuck`): handing one to a stage that is about to be cleared
     * releases the whole reduction in one sample.
     *
     * The answer is its duck writer's, on every chain.
     */
    fun ducksWith(): Boolean = duckWriter?.declared == true

    /**
     * Drops the param state this chain resolved from and puts its writers back to what the chain
     * itself authored: a chain that is not in service holds no voice's map (the allocation-cleanup
     * rule), and it must not REMEMBER one either.
     *
     * The remembering is the sharp edge. A cached chain comes back into service through
     * `Cylinder.beginFade`, which asks [ducksWith] BEFORE the arriving chain's writers have run:
     * a duck writer still holding a previous owner's `duck.orbit` would answer "this chain ducks",
     * be handed the live envelope, and then never be configured, so the orbit would keep ducking
     * off a stage its chain no longer declares.
     *
     * Free in the common case: a chain that resolved from no state has nothing to undo.
     */
    private fun forgetParams() {
        if (resolvedFrom == null) {
            return
        }

        resolvedFrom = null
        resolveCount++

        for (i in writers.indices) {
            writers[i].resolve(null)
        }
    }

    /**
     * True while ANY stage still holds audible energy, asked before an orbit is deactivated so no
     * delay echo or reverb tail is cut. A compare, not a scan (see `TailCeiling`).
     */
    fun hasTail(): Boolean {
        for (i in stages.indices) {
            if (stages[i].hasTail()) {
                return true
            }
        }

        return false
    }

    /**
     * Every stage's [KatalystEffect.measureTail]: the chain's input is silent from now on (it drains
     * after a swap), so a tail ceiling may come down to a measurement. A stage fed by one before it
     * still hears that feed, which its ceiling credits after the measurement as always. Asked by `ChainSwap` while it
     * drains, at most every `ChainSwap.TAIL_MEASURE_SECONDS`.
     */
    fun measureTails() {
        for (i in stages.indices) {
            stages[i].measureTail()
        }
    }

    /**
     * True while a stage holds a tail that can NEVER end on its own, which a stopped engine
     * releases after its hold instead of waiting for (phase 3 step 12 decision (j)). Only a delay
     * can, also inside a `parallel` branch or a region's body: at |feedback| >= 1 it recirculates without loss
     * ([KatalystDelayEffect.sustainsItself]).
     * Every other stage's tail ends: the reverb's comb feedback is at most 0.98 (its size is
     * bounded to 0..1 before it reaches the unit, `Reverb.normalizeSize`), the phaser's feedback is clamped at 0.95
     * (`PhaserCore.MAX_FEEDBACK`), the body and vowel resonators are stable filters, and the
     * compressor's lookahead and a region's conversions hold at most a few frames. The gain, eq and duck
     * hold nothing.
     */
    fun sustainsItself(): Boolean {
        for (i in stages.indices) {
            val stage = stages[i]

            if (stage is KatalystDelayEffect && stage.sustainsItself()) {
                return true
            }

            if (stage is KatalystParallelEffect && stage.sustainsItself()) {
                return true
            }

            if (stage is KatalystRegionEffect && stage.sustainsItself()) {
                return true
            }
        }

        return false
    }
}
